import type { MenuModule, MenuSystem } from '@/types/systems';
import { resolveWorkshopModuleRoute } from '@/utils/workshopModuleRoutes';

/** En klickbar träff i modulsökningen och i manualen. */
export interface MenuSearchItem {
  key: string;
  systemName: string;
  moduleName: string | null;
  fallbackTitle: string;
  route: string;
  icon?: string;
  openInNewWindow: boolean;
}

export interface ManualSystemGroup {
  systemName: string;
  fallbackTitle: string;
  icon?: string;
  entries: MenuSearchItem[];
}

export interface LabeledMenuSearchItem {
  title: string;
  systemTitle: string;
  route: string;
  description?: string | null;
}

function moduleRoute(systemName: string, module: MenuModule): string | undefined {
  return resolveWorkshopModuleRoute(systemName, module.name, module.route);
}

function pushModule(
  items: MenuSearchItem[],
  seen: Set<string>,
  system: MenuSystem,
  module: MenuModule
): void {
  const route = moduleRoute(system.name, module);
  const key = `${system.name}:${module.name}`;
  if (route && !seen.has(key)) {
    seen.add(key);
    items.push({
      key,
      systemName: system.name,
      moduleName: module.name,
      fallbackTitle: module.displayName,
      route,
      icon: module.icon,
      openInNewWindow: system.name === 'realtime',
    });
  }
  for (const child of module.children || []) {
    pushModule(items, seen, system, child);
  }
}

function entriesForSystem(system: MenuSystem): MenuSearchItem[] {
  const items: MenuSearchItem[] = [];
  const seen = new Set<string>();
  for (const module of system.modules || []) {
    pushModule(items, seen, system, module);
  }
  if (items.length === 0 && system.route && !system.route.startsWith('/system/')) {
    items.push({
      key: `${system.name}:`,
      systemName: system.name,
      moduleName: null,
      fallbackTitle: system.displayName,
      route: system.route,
      icon: system.icon,
      openInNewWindow: system.name === 'realtime',
    });
  }
  return items;
}

/** Moduler användaren kan öppna, i menyns ordning. */
export function collectMenuSearchItems(systems: MenuSystem[]): MenuSearchItem[] {
  return systems.flatMap(entriesForSystem);
}

/** Samma moduler, grupperade per system så manualen följer menyn. */
export function groupMenuForManual(systems: MenuSystem[]): ManualSystemGroup[] {
  return systems
    .map((system) => ({
      systemName: system.name,
      fallbackTitle: system.displayName,
      icon: system.icon,
      entries: entriesForSystem(system),
    }))
    .filter((group) => group.entries.length > 0);
}

/** Har posten undermenyer visas listan. Annars visas beskrivningstexten. */
export function manualNextStep(childCount: number): 'list' | 'text' {
  return childCount > 0 ? 'list' : 'text';
}

/** Beskrivningen är manualtexten. Saknas den visas den angivna tomtexten. */
export function manualDescription(
  description: string | null | undefined,
  title: string,
  emptyText: string
): string {
  const text = (description || '').trim();
  if (!text || text === title.trim()) return emptyText;
  return text;
}

/** Längre manualtext vinner. Annars används den korta modulbeskrivningen. */
export function resolveManualBody(
  manualText: string | null | undefined,
  description: string | null | undefined,
  title: string,
  emptyText: string
): string {
  const manual = (manualText || '').trim();
  if (manual) return manual;
  return manualDescription(description, title, emptyText);
}

/** Tom sökning ger ingen lista. Träff på namn, system eller beskrivning, inte på sökvägen. */
export function filterMenuSearchItems<T extends LabeledMenuSearchItem>(items: T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return items.filter((item) =>
    `${item.title} ${item.systemTitle} ${item.description || ''}`.toLowerCase().includes(needle)
  );
}
